import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {Trophy, Medal, Award, Star, ArrowLeft, Zap, Crown, MapPin, TrendingUp, Users} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import { searchCities, IBGECity } from '../data/ibgeCities'
import { getBadgeForCity, REGIONAL_BADGES } from '../data/regionalBadges'

export function RankingMunicipal() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [topDonors, setTopDonors] = useState<any[]>([])
  const [userProfile, setUserProfile] = useState<any>(null)
  const [userRank, setUserRank] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedCity, setSelectedCity] = useState<IBGECity | null>(null)
  const [citySearch, setCitySearch] = useState('')
  const [searchResults, setSearchResults] = useState<IBGECity[]>([])
  const [showSearch, setShowSearch] = useState(false)
  const [totalCityDonors, setTotalCityDonors] = useState(0)

  useEffect(() => {
    if (citySearch.length >= 2) {
      setSearchResults(searchCities(citySearch))
    } else {
      setSearchResults([])
    }
  }, [citySearch])

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return
      
      try {
        const { list: userProfiles } = await lumi.entities.profiles.list({
          filter: { userId: user.userId }
        })
        if (userProfiles.length > 0) {
          const profile = userProfiles[0]
          setUserProfile(profile)
          
          // Definir cidade selecionada como a cidade do usuário
          if (profile.ibgeCityCode) {
            setSelectedCity({
              id: profile.ibgeCityCode,
              name: profile.city,
              state: profile.state,
              stateName: profile.stateName || profile.state
            })
          } else if (profile.city && profile.state) {
            // Fallback se não tiver código IBGE
            setSelectedCity({
              id: '',
              name: profile.city,
              state: profile.state,
              stateName: profile.state
            })
          }
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error)
      }
    }
    fetchUserProfile()
  }, [user])

  useEffect(() => {
    const fetchCityRanking = async () => {
      if (!selectedCity) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        
        // Buscar todos os doadores da cidade
        const filter: any = { 
          isDonor: true,
          city: selectedCity.name,
          state: selectedCity.state
        }
        
        if (selectedCity.id) {
          filter.ibgeCityCode = selectedCity.id
        }

        const { list, total } = await lumi.entities.profiles.list({
          filter,
          sort: { points: -1 },
          limit: 100 // Buscar mais para calcular posição do usuário
        })
        
        setTotalCityDonors(total)
        setTopDonors(list.slice(0, 10))
        
        // Calcular posição do usuário
        if (userProfile) {
          const userPosition = list.findIndex(d => d._id === userProfile._id)
          setUserRank(userPosition !== -1 ? userPosition + 1 : null)
        }
      } catch (error) {
        console.error('Failed to fetch city ranking:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchCityRanking()
  }, [selectedCity, userProfile])

  const getRankIcon = (index: number) => {
    if (index === 0) return <Crown className="h-6 w-6 text-yellow-500" />
    if (index === 1) return <Medal className="h-6 w-6 text-gray-400" />
    if (index === 2) return <Award className="h-6 w-6 text-orange-600" />
    return <Star className="h-5 w-5 text-gray-400" />
  }

  const getRankBg = (index: number) => {
    if (index === 0) return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-300'
    if (index === 1) return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300'
    if (index === 2) return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-300'
    return 'bg-white border-gray-200'
  }

  const selectCity = (city: IBGECity) => {
    setSelectedCity(city)
    setCitySearch('')
    setSearchResults([])
    setShowSearch(false)
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/ranking')}
          className="mb-4"
          aria-label="Voltar para rankings"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center shadow-md">
              <MapPin className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-2">
            🏆 Heróis de {selectedCity ? `${selectedCity.name}/${selectedCity.state}` : '...'}
          </h1>
          <p className="text-gray-600 text-lg">
            Ranking municipal dos maiores doadores
          </p>
          
          {/* Seletor de Cidade */}
          <div className="mt-4 max-w-md mx-auto relative">
            {!showSearch ? (
              <Button 
                variant="outline" 
                onClick={() => setShowSearch(true)}
                className="w-full"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Mudar cidade
              </Button>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  placeholder="Digite o nome da cidade..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoFocus
                  aria-label="Buscar cidade"
                />
                {searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((city) => (
                      <button
                        key={city.id}
                        onClick={() => selectCity(city)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors"
                      >
                        {city.name}/{city.state}
                      </button>
                    ))}
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowSearch(false)
                    setCitySearch('')
                    setSearchResults([])
                  }}
                  className="mt-2"
                >
                  Cancelar
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* User Position Card */}
        {userProfile && selectedCity && userRank && (
          <Card className="mb-8 border-2 border-red-500 bg-gradient-to-r from-red-50 to-white shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center text-white font-black text-xl shadow-md">
                    {userProfile.displayName?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900">{userProfile.displayName}</h3>
                    <p className="text-sm text-gray-600">{userProfile.bloodType} • {userProfile.city}/{userProfile.state}</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-red-600">#{userRank}</div>
                  <p className="text-xs text-gray-600 font-medium">em {selectedCity.name}</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-red-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="flex items-center justify-center space-x-1 mb-1">
                      <Zap className="h-4 w-4 text-yellow-500" />
                      <span className="text-xl font-black text-gray-900">{userProfile.points || 0}</span>
                    </div>
                    <p className="text-xs text-gray-600">pontos</p>
                  </div>
                  <div>
                    <p className="text-xl font-black text-red-600">{userProfile.totalDonations || 0}</p>
                    <p className="text-xs text-gray-600">doações</p>
                  </div>
                  <div>
                    <p className="text-xl font-black text-red-600">{(userProfile.totalDonations || 0) * 4}</p>
                    <p className="text-xs text-gray-600">vidas salvas</p>
                  </div>
                </div>
              </div>

              {userProfile.badges && userProfile.badges.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-bold text-gray-700 mb-2">Suas Medalhas Municipais:</p>
                  <div className="flex flex-wrap gap-2">
                    {userProfile.badges
                      .filter((b: string) => REGIONAL_BADGES[b]?.scope === 'municipal')
                      .map((badge: string) => {
                        const badgeInfo = getBadgeForCity(badge, selectedCity.name)
                        return (
                          <span key={badge} className="px-3 py-1 bg-white rounded-full text-sm font-medium border border-red-200 shadow-sm">
                            {badgeInfo.icon} {badgeInfo.name}
                          </span>
                        )
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* City Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="pt-6 text-center">
              <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <p className="text-3xl font-black text-gray-900">{totalCityDonors}</p>
              <p className="text-sm text-gray-600">doadores ativos</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-3xl font-black text-gray-900">{topDonors.reduce((sum, d) => sum + (d.totalDonations || 0), 0)}</p>
              <p className="text-sm text-gray-600">doações totais</p>
            </CardContent>
          </Card>
        </div>

        {/* Top 10 Municipal Ranking */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl font-black flex items-center">
              <Trophy className="h-6 w-6 text-yellow-500 mr-2" />
              Top 10 de {selectedCity?.name || '...'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : topDonors.length > 0 ? (
              <div className="space-y-3">
                {topDonors.map((donor, index) => (
                  <div 
                    key={donor._id} 
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all hover:shadow-md ${getRankBg(index)}`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {getRankIcon(index)}
                      </div>
                      <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                        {donor.displayName?.charAt(0) || 'D'}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">{donor.displayName}</h4>
                        <p className="text-sm text-gray-600">
                          {donor.bloodType} • {donor.totalDonations || 0} {(donor.totalDonations || 0) === 1 ? 'doação' : 'doações'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center justify-end space-x-1 mb-1">
                        <Zap className="h-4 w-4 text-yellow-500" />
                        <span className="text-xl font-black text-gray-900">{donor.points || 0}</span>
                      </div>
                      <p className="text-xs text-gray-600 font-medium">pontos</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Seja o primeiro herói de {selectedCity?.name}!</h3>
                <p className="text-gray-600 mb-4">Confirme uma doação e entre para a história da sua cidade</p>
                <Button onClick={() => navigate('/solicitacoes')} className="mt-2">
                  Ver Pedidos de Doação
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/solicitacoes')}
            className="w-full max-w-md h-14 text-lg font-bold bg-red-600 hover:bg-red-700 text-white"
          >
            Ver Pedidos e Doar Agora
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  )
}
